import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import { Table, Tag } from 'antd'
import { DeleteAndUpdate, InputCell, SelectCell } from 'modules/core/components'
import { TableActions } from './components'
import { onSaveChanges } from './utils'
import societies from 'modules/societies'
import { useFeatureIsOn } from '@growthbook/growthbook-react'

const SocietyTable = ({
  editSociety,
  onFetchSocietiesList,
  dataSource,
  loading,
  pagination,
  count,
  onChange,
  tagsList,
}) => {
  const enableTags = useFeatureIsOn('ab_superadmin_tags')
  const [editRow, setEditRow] = useState(null)
  const [dataCellEdit, setDataCellEdit] = useState({})
  const [isLoading, setIsLoading] = useState(false)
  const { t } = useTranslation()

  useEffect(() => {
    return () => {
      setEditRow(null)
      setDataCellEdit({})
      setIsLoading(false)
    }
  }, [])

  const onCellEdit = (name, value) => setDataCellEdit({ ...dataCellEdit, [`${name}`]: value })

  const onCancel = () => setEditRow(null)

  const onConfirm = () => {
    setEditRow(null)
    setDataCellEdit({})
    setIsLoading(false)
  }

  const columns = [
    {
      title: t('FIELD_NAME'),
      dataIndex: 'name',
    },
    {
      title: t('FIELD_DESCRIPTION'),
      dataIndex: 'description',
      render: (description, row) => {
        if (editRow && editRow === row.id) {
          return (
            <InputCell
              initialValue={description}
              onCellUpdate={onCellEdit}
              name="description"
              style={{ width: '100%' }}
            />
          )
        }
        return description
      },
    },
    ...(enableTags
      ? [
          {
            title: t('FIELD_TAGS'),
            dataIndex: 'tags',
            render: (tags, row) => {
              if (editRow && editRow === row.id) {
                return (
                  <SelectCell
                    initialValue={tags}
                    onCellUpdate={onCellEdit}
                    name="tags"
                    mode="multiple"
                    maxTagCount={1}
                    maxTagTextLength={8}
                    showArrow
                    allowClear
                    options={tagsList}
                  />
                )
              }
              return tags.map((el) => (
                <Tag color={tagsList.find((tag) => tag.id === el)?.color}>
                  {tagsList.find((tag) => tag.id === el)?.name}
                </Tag>
              ))
            },
          },
        ]
      : []),
    {
      title: t('FIELD_STATUS'),
      dataIndex: 'status',
      render: (status) => (status ? t('STATE_TAG_ACTIVE') : t('STATE_TAG_INACTIVE')),
    },
    {
      title: t('FIELD_ACTIONS'),
      dataIndex: 'id',
      align: 'right',
      width: 150,
      render: (id) => {
        return id !== editRow ? (
          <TableActions
            onFetchSocietiesList={onFetchSocietiesList}
            id={id}
            setEditRow={setEditRow}
            setIsLoading={setIsLoading}
          />
        ) : (
          <DeleteAndUpdate
            onCancel={onCancel}
            onConfirm={onConfirm}
            onSave={() => {
              const row = dataSource.find((el) => el.id === id)
              onSaveChanges({
                editSociety: () => editSociety(id, { ...row, ...dataCellEdit }),
                setIsLoading,
                onRefetch: onFetchSocietiesList,
                onClose: onConfirm,
              })
            }}
            dataCellEdit={dataCellEdit}
            loading={isLoading}
          />
        )
      },
    },
  ]

  return (
    <Table
      rowKey="id"
      size="small"
      bordered
      columns={columns}
      loading={loading || isLoading}
      dataSource={dataSource}
      onChange={onChange}
      pagination={{
        pageSize: pagination.pageSize,
        size: 'small',
        total: count,
        current: pagination.page,
        showSizeChanger: true,
        pageSizeOptions: ['10', '20', '30'],
      }}
    />
  )
}

const mapDispatchToProps = {
  editSociety: societies.actions.editSociety,
}

export default connect(null, mapDispatchToProps)(SocietyTable)
